import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '../components/ui';
import QueryBar from '../components/QueryBar';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface AuditLogsProps {
  apiUrl: string;
}

// Default view: last 24h, so the screen lands with data instead of a blank table.
const DEFAULT_QUERY = 'earliest=24h latest=now';

interface AuditLog {
  id: number;
  timestamp: string;
  service: string;
  tool_name: string;
  status: string;
  result: string;
  agent_id: string;
  org_id: string;
  response_status: string;
  arguments?: string;
  device_hostname?: string;
  display_name?: string;
  row_hash?: string;
  prev_hash?: string;
  end_user?: string;
  detail?: string;
}

interface VerdictSpan {
  category: string;
  match: string;
  context: string;
  location: string;
}

interface AuditDetail {
  identity?: { user: string | null; groups: string[]; verified: boolean; source: string };
  verdict?: { action: string; detectors?: string[]; score?: number; spans?: VerdictSpan[]; reason?: string; kind?: string };
  input_dlp?: { types: string[] };
  output_dlp?: { mode: string; types: string[] };
}

const parseDetail = (raw?: string): AuditDetail | null => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

const prettyJson = (raw?: string): string => {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
};

/** The span's context with the flagged text highlighted. */
const HighlightedSpan: React.FC<{ span: VerdictSpan }> = ({ span }) => {
  const i = span.context.indexOf(span.match);
  if (i === -1) return <mark className="audit-why-mark">{span.match}</mark>;
  return (
    <>
      {span.context.slice(0, i)}
      <mark className="audit-why-mark">{span.match}</mark>
      {span.context.slice(i + span.match.length)}
    </>
  );
};

interface ChainVerifyResult {
  enabled: boolean;
  ok: boolean;
  rowsChecked: number;
  firstBreakId: number | null;
  detail: string;
}

const PAGE_SIZE = 25;

const statusBadge = (status: string, t: (key: string) => string): { cls: string; label: string } => {
  const s = (status || '').toLowerCase();
  if (s === 'success' || s === 'policy_allowed') return { cls: 'badge-success', label: t('pages.auditLogs.success') };
  if (s === 'error') return { cls: 'badge-error', label: t('pages.auditLogs.error') };
  if (s.startsWith('mcp_injection')) return { cls: 'badge-error', label: t('pages.auditLogs.injection') };
  if (s === 'mcp_tool_poisoned') return { cls: 'badge-error', label: t('pages.auditLogs.toolPoisoned') };
  if (s.includes('den') || s.includes('block')) return { cls: 'badge-error', label: t('pages.auditLogs.blocked') };
  if (s.includes('warn') || s.includes('anomal')) return { cls: 'badge-warning', label: t('pages.auditLogs.warning') };
  return { cls: 'badge-info', label: status || 'Unknown' };
};

const AuditLogs: React.FC<AuditLogsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [summary, setSummary] = useState({ total: 0, success: 0, errors: 0, violations: 0, uniqueAgents: 0, uniqueTools: 0 });
  const [timeline, setTimeline] = useState<{ success: number; errors: number }[]>([]);
  const [chainStatus, setChainStatus] = useState<ChainVerifyResult | null>(null);
  const [verifyingChain, setVerifyingChain] = useState(false);

  const verifyChain = useCallback(async () => {
    try {
      setVerifyingChain(true);
      const response = await fetch(`${apiUrl}/admin/audit-logs/verify-chain`);
      const data = await response.json();
      if (response.ok) setChainStatus(data);
    } catch (err) {
      console.error('Failed to verify audit chain:', err);
    } finally {
      setVerifyingChain(false);
    }
  }, [apiUrl]);

  const fetchLogs = useCallback(async (q: string, off: number) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ q, limit: String(PAGE_SIZE), offset: String(off), sort: 'DESC' });
      const response = await fetch(`${apiUrl}/admin/audit-logs/search?${params}`);
      const data = await response.json();
      if (!response.ok) {
        // Surface parser/validation errors (400) instead of silently showing nothing.
        setError(data.error || 'Query failed');
        setLogs([]);
        setTotal(0);
        return;
      }
      setError('');
      setLogs(
        (data.logs || []).map((log: any) => ({
          id: log.id || 0,
          timestamp: String(log.timestamp || ''),
          service: String(log.service || ''),
          tool_name: String(log.tool_name || ''),
          status: String(log.status || ''),
          result: String(log.result || ''),
          agent_id: String(log.agent_id || ''),
          org_id: String(log.org_id || ''),
          response_status: String(log.response_status || ''),
          arguments: String(log.arguments || ''),
          device_hostname: log.device_hostname ? String(log.device_hostname) : undefined,
          display_name: log.display_name ? String(log.display_name) : undefined,
          row_hash: log.row_hash ? String(log.row_hash) : undefined,
          prev_hash: log.prev_hash ? String(log.prev_hash) : undefined,
          end_user: log.end_user ? String(log.end_user) : undefined,
          detail: log.detail ? String(log.detail) : undefined,
        }))
      );
      setTotal(data.total || 0);
      setSummary(data.summary || { total: 0, success: 0, errors: 0, violations: 0, uniqueAgents: 0, uniqueTools: 0 });
      setTimeline(data.timeline || []);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Could not reach the firewall API.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Land on the default (last-24h) view with results ready.
  useEffect(() => {
    fetchLogs(DEFAULT_QUERY, 0);
    verifyChain();
  }, [fetchLogs, verifyChain]);

  const handleSearch = (q: string) => {
    setQuery(q);
    setOffset(0);
    fetchLogs(q, 0);
  };

  // Build timeline from backend's hourly buckets
  const timelineDisplay = timeline.length > 0 ? timeline : Array(24).fill({ success: 0, errors: 0 });
  const timelineWithLabels = timelineDisplay.map((b: any, i: number) => {
    const h = i;
    return {
      label: h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`,
      success: b.success || 0,
      errors: b.errors || 0,
    };
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const start = offset;

  return (
    <div className="page-content audit-logs-page">
      <div className="audit-header">
        <div>
          <h1><Icon name="logs" size={22} /> {t('pages.auditLogs.auditLogs')}</h1>
          <p className="audit-subtitle">{t('pages.auditLogs.completeAuditTrailOfAllFirewall')}</p>
        </div>
      </div>

      <QueryBar apiUrl={apiUrl} onSearch={handleSearch} initialQuery={DEFAULT_QUERY} />

      {error && (
        <div className="audit-query-error">
          <Icon name="warning" size={14} /> {error}
        </div>
      )}

      {/* ── Summary Cards ── */}
      <div className="audit-summary">
        <div className="audit-stat">
          <div className="audit-stat-num">{summary.total}</div>
          <div className="audit-stat-label">{t('pages.auditLogs.totalEvents')}</div>
        </div>
        <div className="audit-stat audit-stat-success">
          <div className="audit-stat-num">{summary.success}</div>
          <div className="audit-stat-label">{t('pages.auditLogs.successful')}</div>
        </div>
        <div className="audit-stat audit-stat-error">
          <div className="audit-stat-num">{summary.violations}</div>
          <div className="audit-stat-label">{t('pages.auditLogs.violations')}</div>
        </div>
        <div className="audit-stat">
          <div className="audit-stat-num">{summary.uniqueAgents}</div>
          <div className="audit-stat-label">{t('pages.auditLogs.agents')}</div>
        </div>
        <div className="audit-stat">
          <div className="audit-stat-num">{summary.uniqueTools}</div>
          <div className="audit-stat-label">{t('pages.auditLogs.toolsUsed')}</div>
        </div>
        <div
          className={`audit-stat ${
            chainStatus && chainStatus.enabled ? (chainStatus.ok ? 'audit-stat-success' : 'audit-stat-error') : ''
          }`}
          title={chainStatus?.detail || t('pages.auditLogs.verifyingTheTamperEvidentHashChain')}
        >
          <div className="audit-stat-num">
            <Icon name={chainStatus?.enabled && !chainStatus.ok ? 'warning' : 'lock'} size={18} />
          </div>
          <div className="audit-stat-label">
            {verifyingChain
              ? t('pages.auditLogs.verifyingChain')
              : !chainStatus
                ? t('pages.auditLogs.chainIntegrity')
                : !chainStatus.enabled
                  ? t('pages.auditLogs.chainingDisabled')
                  : chainStatus.ok
                    ? t('pages.auditLogs.chainIntact')
                    : `Chain broken (row ${chainStatus.firstBreakId})`}
          </div>
        </div>
      </div>

      {/* ── Activity Timeline ── */}
      <div className="audit-timeline-card">
        <div className="audit-timeline-head">
          <h3>{t('pages.auditLogs.activityTimeline')}</h3>
          <span className="audit-badge">{t('pages.auditLogs.last24Hours')}</span>
        </div>
        <div className="audit-timeline-chart">
          {(() => {
            const maxVal = Math.max(...timelineWithLabels.map(b => b.success + b.errors), 1);
            return timelineWithLabels.map((b, i) => {
              const total = b.success + b.errors;
              const height = Math.max(2, (total / maxVal) * 60);
              const successH = total > 0 ? (b.success / total) * height : 0;
              const errorH = total > 0 ? (b.errors / total) * height : 0;
              return (
                <div key={i} className="audit-tl-col" title={t('pages.auditLogs.timelineBucket', { label: b.label, success: b.success, errors: b.errors })}>
                  <div className="audit-tl-bar-stack" style={{ height: `${height}px` }}>
                    {errorH > 0 && <div className="audit-tl-bar audit-tl-error" style={{ height: `${errorH}px` }} />}
                    {successH > 0 && <div className="audit-tl-bar audit-tl-success" style={{ height: `${successH}px` }} />}
                  </div>
                  {i % 3 === 0 && <span className="audit-tl-label">{b.label}</span>}
                </div>
              );
            });
          })()}
        </div>
        <div className="audit-timeline-legend">
          <span><span className="audit-dot" style={{ background: 'var(--success)' }} /> {t('pages.auditLogs.success')}</span>
          <span><span className="audit-dot" style={{ background: 'var(--danger)' }} /> {t('pages.auditLogs.errorViolation')}</span>
        </div>
      </div>

      {/* ── Results count ── */}
      <div className="audit-results-bar">
        <span>{t('pages.auditLogs.eventsFound', { count: total })}</span>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="audit-loading">
          <div className="audit-spinner" />
          <p>{t('pages.auditLogs.loadingAuditLogs')}</p>
        </div>
      ) : logs.length > 0 ? (
        <>
          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{t('pages.auditLogs.timestamp')}</th>
                  <th>{t('pages.auditLogs.service')}</th>
                  <th>{t('pages.auditLogs.toolAction')}</th>
                  <th>{t('pages.auditLogs.agentDevice')}</th>
                  <th>{t('pages.auditLogs.status')}</th>
                  <th>{t('pages.auditLogs.result')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const badge = statusBadge(log.status, t);
                  const isExpanded = expandedLog === log.id;
                  const timeAgo = (() => {
                    const diff = Date.now() - new Date(log.timestamp).getTime();
                    if (diff < 60000) return 'just now';
                    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                    return `${Math.floor(diff / 86400000)}d ago`;
                  })();
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`audit-row ${isExpanded ? 'audit-row-expanded' : ''}`}
                        onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                      >
                        <td className="audit-id">{log.id}</td>
                        <td>
                          <div className="audit-ts">
                            <span className="audit-ts-date">{parseServerDate(log.timestamp).toLocaleDateString()}</span>
                            <span className="audit-ts-time">{parseServerDate(log.timestamp).toLocaleTimeString()}</span>
                            <span className="audit-ts-ago">{timeAgo}</span>
                          </div>
                        </td>
                        <td><span className="audit-service-tag">{log.service}</span></td>
                        <td className="audit-tool">{(log.tool_name || '-').replace(/_/g, ' ').replace(/\bdlp\b/i, 'DLP')}</td>
                        <td className="audit-agent" title={log.agent_id}>
                          <span className="audit-agent-name">{log.end_user || log.device_hostname || log.display_name || log.agent_id || '-'}</span>
                          {log.end_user && <span className="audit-agent-id">{log.display_name || log.agent_id}</span>}
                          {(log.device_hostname || log.display_name) && log.agent_id && log.agent_id !== (log.device_hostname || log.display_name) && (
                            <span className="audit-agent-id">{log.agent_id}</span>
                          )}
                        </td>
                        <td><span className={`audit-badge ${badge.cls}`}>{badge.label}</span></td>
                        <td className="audit-result" title={log.result}>{log.result && log.result.length > 50 ? log.result.slice(0, 50) + '...' : (log.result || '-')}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="audit-detail-row">
                          <td colSpan={7}>
                            <div className="audit-detail">
                              <div className="audit-detail-grid">
                                <div><strong>{t('pages.auditLogs.id')}</strong> {log.id}</div>
                                <div><strong>{t('pages.auditLogs.timestamp2')}</strong> {parseServerDate(log.timestamp).toLocaleString()}</div>
                                <div><strong>{t('pages.auditLogs.service2')}</strong> {log.service}</div>
                                <div><strong>{t('pages.auditLogs.tool')}</strong> {log.tool_name}</div>
                                <div><strong>{t('pages.auditLogs.agent')}</strong> {log.agent_id}</div>
                                {log.end_user && <div><strong>{t('pages.auditLogs.endUser')}</strong> {log.end_user}</div>}
                                {(log.device_hostname || log.display_name) && (
                                  <div><strong>{t('pages.auditLogs.device')}</strong> {log.device_hostname || log.display_name}</div>
                                )}
                                <div><strong>{t('pages.auditLogs.status2')}</strong> {log.status}</div>
                                <div><strong>{t('pages.auditLogs.httpStatus')}</strong> {log.response_status || '-'}</div>
                                <div><strong>{t('pages.auditLogs.organization')}</strong> {log.org_id || t('pages.auditLogs.default')}</div>
                                {log.row_hash && (
                                  <div title={log.prev_hash || undefined}>
                                    <strong>{t('pages.auditLogs.chainHash')}</strong> <code>{log.row_hash.slice(0, 16)}…</code>
                                  </div>
                                )}
                              </div>
                              {(() => {
                                const d = parseDetail(log.detail);
                                if (!d) return null;
                                const v = d.verdict;
                                return (
                                  <>
                                    {v && (
                                      <div className="audit-why">
                                        <div className="audit-why-title">{t('pages.auditLogs.whyFlagged')}</div>
                                        <div className="audit-why-grid">
                                          <div><strong>{t('pages.auditLogs.actionTaken')}</strong> {t(`pages.auditLogs.action_${v.action}`, { defaultValue: v.action })}</div>
                                          {v.detectors && v.detectors.length > 0 && (
                                            <div><strong>{t('pages.auditLogs.detectors')}</strong> {v.detectors.map((x) => <code key={x} className="audit-why-chip">{x}</code>)}</div>
                                          )}
                                          {typeof v.score === 'number' && <div><strong>{t('pages.auditLogs.score')}</strong> {v.score.toFixed(2)}</div>}
                                          {v.reason && <div><strong>{t('pages.auditLogs.reason')}</strong> {v.reason}</div>}
                                        </div>
                                        {(v.spans || []).map((sp, i) => (
                                          <div key={i} className="audit-why-span">
                                            <div className="audit-why-span-head">
                                              {t('pages.auditLogs.flaggedText')} · <code>{sp.category}</code> {t('pages.auditLogs.location')} <code>{sp.location}</code>
                                            </div>
                                            <pre className="audit-why-context"><HighlightedSpan span={sp} /></pre>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {(d.identity || d.input_dlp || d.output_dlp) && (
                                      <div className="audit-detail-grid">
                                        {d.identity && (
                                          <div>
                                            <strong>{t('pages.auditLogs.identity')}</strong> {d.identity.user || '-'}{' '}
                                            ({d.identity.verified ? t('pages.auditLogs.verified') : t('pages.auditLogs.unverified')})
                                          </div>
                                        )}
                                        {d.identity && d.identity.groups.length > 0 && (
                                          <div><strong>{t('pages.auditLogs.groups')}</strong> {d.identity.groups.slice(0, 8).join(', ')}{d.identity.groups.length > 8 ? '…' : ''}</div>
                                        )}
                                        {d.input_dlp && <div><strong>{t('pages.auditLogs.inputDlp')}</strong> {d.input_dlp.types.join(', ')}</div>}
                                        {d.output_dlp && <div><strong>{t('pages.auditLogs.outputDlp')}</strong> {d.output_dlp.types.join(', ')} ({d.output_dlp.mode})</div>}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                              {log.arguments && (
                                <div className="audit-detail-result">
                                  <strong>{log.detail ? t('pages.auditLogs.toolInputs') : t('pages.auditLogs.arguments')}</strong>
                                  <pre>{log.detail ? prettyJson(log.arguments) : log.arguments}</pre>
                                </div>
                              )}
                              <div className="audit-detail-result">
                                <strong>{log.detail ? t('pages.auditLogs.toolOutput') : t('pages.auditLogs.fullResult')}</strong>
                                <pre>{(log.detail ? prettyJson(log.result) : log.result) || 'No details available'}</pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="audit-pagination">
            <span className="audit-page-info">{t('pages.auditLogs.showingOf', { v0: start + 1, v1: Math.min(start + PAGE_SIZE, total), v2: total })}</span>
            <div className="audit-page-controls">
              <button className="ent-btn sm" onClick={() => { setOffset(0); fetchLogs(query, 0); }} disabled={currentPage === 1}>&laquo;</button>
              <button className="ent-btn sm" onClick={() => { const newOff = Math.max(0, offset - PAGE_SIZE); setOffset(newOff); fetchLogs(query, newOff); }} disabled={currentPage === 1}>{t('pages.auditLogs.prev')}</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, startPage + 4);
                startPage = Math.max(1, endPage - 4);
                return startPage + i;
              }).filter(p => p >= 1 && p <= totalPages).map(p => (
                <button key={p} className={`ent-btn sm${p === currentPage ? ' primary' : ''}`} onClick={() => { const newOff = (p - 1) * PAGE_SIZE; setOffset(newOff); fetchLogs(query, newOff); }}>
                  {p}
                </button>
              ))}
              <button className="ent-btn sm" onClick={() => { const newOff = Math.min((totalPages - 1) * PAGE_SIZE, offset + PAGE_SIZE); setOffset(newOff); fetchLogs(query, newOff); }} disabled={currentPage === totalPages}>{t('pages.auditLogs.next')}</button>
              <button className="ent-btn sm" onClick={() => { const newOff = (totalPages - 1) * PAGE_SIZE; setOffset(newOff); fetchLogs(query, newOff); }} disabled={currentPage === totalPages}>&raquo;</button>
            </div>
          </div>
        </>
      ) : (
        <div className="audit-empty">
          <Icon name="checkCircle" size={32} />
          <h3>{t('pages.auditLogs.noAuditLogsFound')}</h3>
          <p>{t('pages.auditLogs.noEventsMatchYourSearchTry')}</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
